import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { SkeletonTable } from "@/components/KovaSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Users, ShieldCheck, KeyRound, Send, Plus, RefreshCw, UserX, UserCheck, Trash2, AlertTriangle } from "lucide-react";

const SYSTEM_ROLES = [
  { key: "admin", label: "Admin" },
  { key: "co_admin", label: "Co-Admin" },
  { key: "owner", label: "Owner" },
  { key: "accountant", label: "Accountant" },
  { key: "sales_manager", label: "Sales Manager" },
  { key: "sales_rep", label: "Sales Representative" },
  { key: "marketing_head", label: "Marketing Head" },
  { key: "marketing_rep", label: "Marketing Representative" },
  { key: "service_manager", label: "Service Manager" },
  { key: "spare_parts_manager", label: "Spare Parts Manager" },
  { key: "technician", label: "Technician (no login)" },
  { key: "subsidy_desk_manager", label: "Subsidy Desk Manager" },
  { key: "subsidy_desk_executive", label: "Subsidy Desk Executive" },
];
const NO_LOGIN_SYS_ROLES = new Set(["technician"]);
const ROLE_LABEL = Object.fromEntries(SYSTEM_ROLES.map((r) => [r.key, r.label]));

// Convert any backend error payload into a human-readable string.
// Pydantic validation errors arrive as an array of {loc, msg, type}; the
// previous handler stringified them as "[object Object]" and surfaced a
// generic failure toast.
function parseApiError(e, fallback = "Request failed") {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const msgs = d.map((x) => {
      const field = Array.isArray(x?.loc) ? x.loc.filter((p) => p !== "body").join(".") : "";
      const msg = x?.msg || "invalid value";
      return field ? `${field}: ${msg}` : msg;
    });
    return msgs.join(" • ");
  }
  return fallback;
}

function formatWhen(iso) {
  if (!iso) return "Never";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const TABS = [
  { id: "staff", label: "Staff & Roles" },
  { id: "teams", label: "Teams" },
  { id: "roles", label: "Roles" },
  { id: "notify",label: "Send Notification" },
];

function Chip({ children, tone = "muted" }) {
  const map = {
    green:  "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25",
    yellow: "bg-yellow-500/10 text-yellow-300 border-yellow-500/25",
    red:    "bg-red-500/10 text-red-300 border-red-500/25",
    muted:  "bg-white/5 text-[#8A8F98] border-white/10",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-heading font-semibold px-2 py-0.5 rounded-full border ${map[tone]}`}>{children}</span>;
}

export default function TeamAccess() {
  const [tab, setTab] = useState("staff");
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [u, t, r, c] = await Promise.all([
      api.get("/users"),
      api.get("/teams"),
      api.get("/roles"),
      api.get("/permissions/catalog"),
    ]);
    setUsers(u.data); setTeams(t.data); setRoles(r.data); setCatalog(c.data.modules);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-8 lg:p-10 max-w-[1500px]">
      <PageHeader
        title="Team & Access"
        subtitle="Teams, staff accounts, custom roles and internal notifications — the foundation KOVA AI will build on."
        eyebrow="Admin"
        testid="page-team-access"
      />

      <div className="border-b border-[#3A4640] mb-6 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`ta-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-heading font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-[#39FF14] text-[#F8F9FA]" : "border-transparent text-[#8A8F98] hover:text-[#F8F9FA]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "staff"  && <StaffTab   users={users}  teams={teams} roles={roles} loading={loading} reload={load} />}
      {tab === "teams"  && <TeamsTab   teams={teams}  reload={load} />}
      {tab === "roles"  && <RolesTab   roles={roles}  catalog={catalog} reload={load} />}
      {tab === "notify" && <NotifyTab  teams={teams}  roles={roles} users={users} />}
    </div>
  );
}

/* ------------------ Staff & Roles ------------------ */
function StaffTab({ users, teams, roles, loading, reload }) {
  const [q, setQ] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [adding, setAdding] = useState(false);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return users.filter((u) => {
      if (t && ![u.name, u.email, u.role, ROLE_LABEL[u.role] || ""].some((x) => (x || "").toLowerCase().includes(t))) return false;
      if (teamFilter && u.team_id !== teamFilter) return false;
      if (roleFilter && u.role_id !== roleFilter) return false;
      if (statusFilter === "active" && u.is_paused) return false;
      if (statusFilter === "disabled" && !u.is_paused) return false;
      return true;
    });
  }, [users, q, teamFilter, roleFilter, statusFilter]);

  return (
    <div>
      {/* Hero summary — total / active / disabled */}
      <div className="mb-4 rounded-xl border border-[#3A4640] bg-[#121815] px-4 py-3 flex flex-wrap items-center gap-4"
           data-testid="ta-staff-hero">
        <div className="flex items-center gap-2 text-[#F8F9FA]">
          <Users size={16} className="text-[#39FF14]" />
          <span className="text-xl font-heading font-bold" data-testid="ta-staff-count-total">{users.length}</span>
          <span className="text-[11px] uppercase tracking-wider text-[#8A8F98]">Employees</span>
        </div>
        <span className="h-5 w-px bg-[#3A4640]" />
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[#39FF14] shadow-[0_0_5px_rgba(57,255,20,0.5)]" />
          <span className="text-sm text-[#F8F9FA]" data-testid="ta-staff-count-active">
            {users.filter((u) => !u.is_paused).length}
          </span>
          <span className="text-[11px] uppercase tracking-wider text-[#8A8F98]">Active</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[#8A8F98]" />
          <span className="text-sm text-[#F8F9FA]" data-testid="ta-staff-count-disabled">
            {users.filter((u) => u.is_paused).length}
          </span>
          <span className="text-[11px] uppercase tracking-wider text-[#8A8F98]">Disabled</span>
        </div>
      </div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2 items-center">
          <Input placeholder="Search name, email, role…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ta-staff-search"
            className="max-w-[260px] bg-[#121815] border-[#3A4640] text-[#F8F9FA] rounded-[10px]" />
          <Select value={teamFilter || "__all__"} onValueChange={(v) => setTeamFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="ta-staff-filter-team" className="w-[160px] bg-[#121815] border-[#3A4640] rounded-[10px]"><SelectValue placeholder="All teams" /></SelectTrigger>
            <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
              <SelectItem value="__all__">All teams</SelectItem>
              {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={roleFilter || "__all__"} onValueChange={(v) => setRoleFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="ta-staff-filter-role" className="w-[160px] bg-[#121815] border-[#3A4640] rounded-[10px]"><SelectValue placeholder="All roles" /></SelectTrigger>
            <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
              <SelectItem value="__all__">All roles</SelectItem>
              {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="ta-staff-filter-status" className="w-[140px] bg-[#121815] border-[#3A4640] rounded-[10px]"><SelectValue placeholder="Any status" /></SelectTrigger>
            <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
              <SelectItem value="__all__">Any status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setAdding(true)} data-testid="ta-add-staff-btn"
          className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]">
          <Plus size={14} className="mr-2"/>Add Staff
        </Button>
      </div>
      {loading ? <SkeletonTable rows={6} cols={7} /> : (
      <div className="tzat-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#121815] border-b border-[#3A4640] text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Login</th>
              <th className="text-left px-4 py-3">Team</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Last Login</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6">
                <EmptyState icon={Users} title="No staff match" description="Adjust your search or add a new team member." testid="ta-staff-empty" />
              </td></tr>
            )}
            {filtered.map((u) => {
              const team = teams.find((t) => t.id === u.team_id);
              const role = roles.find((r) => r.id === u.role_id);
              const paused = u.is_paused;
              return (
                <tr key={u.id} className="border-b border-[#3A4640] last:border-0 hover:bg-[#121815]/60 transition-colors" data-testid={`ta-staff-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#F8F9FA]">{u.name || "(no name)"}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">{ROLE_LABEL[u.role] || u.role}</div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-[#8A8F98]">{u.email}</td>
                  <td className="px-4 py-3 text-sm text-[#F8F9FA]">{team?.name || <span className="text-[#8A8F98]">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-[#F8F9FA]">{role?.name || <span className="text-[#8A8F98]">—</span>}</td>
                  <td className="px-4 py-3">{paused ? <Chip tone="yellow">Disabled</Chip> : <Chip tone="green">Active</Chip>}
                    {u.must_reset_password && <span className="ml-2"><Chip tone="yellow">Reset required</Chip></span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#8A8F98]" data-testid={`ta-staff-lastlogin-${u.id}`}>{formatWhen(u.last_login_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <StaffActions user={u} teams={teams} roles={roles} reload={reload} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>)}
      <AddStaffDialog open={adding} onOpenChange={setAdding} teams={teams} roles={roles} reload={reload} />
    </div>
  );
}

function AddStaffDialog({ open, onOpenChange, teams, roles, reload }) {
  const empty = useMemo(() => ({ name: "", email: "", phone: "", role: "sales_rep", team_id: "", role_id: "", password: "", must_reset_password: false }), []);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [createdPassword, setCreatedPassword] = useState(""); // one-time copy, cleared on close

  useEffect(() => {
    if (open) { setForm(empty); setCreated(null); setCreatedPassword(""); }
  }, [open, empty]);
  const noLogin = NO_LOGIN_SYS_ROLES.has(form.role);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Full name is required"); return; }
    if (!form.email.trim()) { toast.error("Login email is required"); return; }
    // Minimal client-side email shape check — catches most mistypes before the server trip.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Enter a valid email (e.g. name@example.com)"); return;
    }
    if (!noLogin && (!form.password || form.password.length < 6)) {
      toast.error("Initial password must be at least 6 characters"); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        role: form.role,
        team_id: form.team_id || null,
        role_id: form.role_id || null,
        must_reset_password: !noLogin && !!form.must_reset_password,
        password: noLogin ? "" : form.password,
      };
      const { data } = await api.post("/users", payload);
      setCreated(data);
      setCreatedPassword(noLogin ? "" : form.password);  // keep locally for one-time copy
      toast.success(`${data.name || data.email} added`);
      reload();
    } catch (e) {
      toast.error(parseApiError(e, "Failed to create staff"));
    } finally { setSaving(false); }
  };

  const copyLoginDetails = async () => {
    if (!created) return;
    const lines = [
      `Name: ${created.name}`,
      `Login: ${created.email}`,
      createdPassword ? `Initial password: ${createdPassword}` : null,
      `Role: ${ROLE_LABEL[created.role] || created.role}`,
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Login details copied to clipboard");
    } catch {
      toast.error("Could not access clipboard — please copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); }}>
      <DialogContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA] max-w-xl" data-testid="ta-add-staff-dialog">
        <DialogHeader>
          <DialogTitle>{created ? "Staff account created" : "Add staff"}</DialogTitle>
          <DialogDescription className="text-[#8A8F98]">
            {created ? "Share the login details below with the new team member." : "Create a new login for a KOVA staff member. Password is stored securely (hashed)."}
          </DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="space-y-3" data-testid="ta-add-staff-success">
            <div className="tzat-card p-4 space-y-2 border-[#39FF14]/30">
              <Row label="Name"    value={created.name} />
              <Row label="Login"   value={created.email} testid="ta-add-staff-success-email" />
              <Row label="Team"    value={teams.find((t) => t.id === created.team_id)?.name || "—"} />
              <Row label="Role"    value={roles.find((r) => r.id === created.role_id)?.name || ROLE_LABEL[created.role] || created.role} />
              <Row label="Status"  value={created.is_paused ? "Disabled" : "Active"} />
              {created.must_reset_password && <Row label="First login" value="Must reset password on first sign-in" />}
              {createdPassword && (
                <div className="pt-2 mt-2 border-t border-[#3A4640] space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">One-time password handoff</div>
                  <div className="flex items-center justify-between gap-3 rounded-md bg-[#0B0F0C] border border-[#3A4640] px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-[11px] text-[#8A8F98]">Initial password</div>
                      <div className="font-mono text-sm text-[#F8F9FA] truncate" data-testid="ta-add-staff-success-password">
                        {createdPassword}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={copyLoginDetails} data-testid="ta-add-staff-copy-login"
                      className="bg-transparent border-[#3A4640] text-[#F8F9FA] hover:border-[#39FF14] hover:text-[#39FF14] rounded-[10px]">
                      Copy login details
                    </Button>
                  </div>
                  <p className="text-[11px] text-[#8A8F98]">
                    Share these with the new team member now. Once this dialog closes the password is <b>not recoverable</b> —
                    stored only as a secure hash. Use <b>Reset Password</b> later if they forget.
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} data-testid="ta-add-staff-done"
                className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]">Done</Button>
            </DialogFooter>
          </div>
        ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Full name*</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ta-add-staff-name" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Login email*</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ta-add-staff-email" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" data-testid="ta-add-staff-phone" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">System role*</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, password: NO_LOGIN_SYS_ROLES.has(v) ? "" : form.password })}>
                <SelectTrigger data-testid="ta-add-staff-role" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
                  {SYSTEM_ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Team</Label>
              <Select value={form.team_id || "__none__"} onValueChange={(v) => setForm({ ...form, team_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="ta-add-staff-team" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]"><SelectValue placeholder="No team" /></SelectTrigger>
                <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
                  <SelectItem value="__none__">— No team —</SelectItem>
                  {teams.filter((t) => t.active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Custom role</Label>
              <Select value={form.role_id || "__none__"} onValueChange={(v) => setForm({ ...form, role_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="ta-add-staff-role-id" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]"><SelectValue placeholder="No custom role" /></SelectTrigger>
                <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
                  <SelectItem value="__none__">— No custom role —</SelectItem>
                  {roles.filter((r) => r.active).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {!noLogin ? (
            <div className="pt-2 border-t border-[#3A4640]">
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Initial password*</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="ta-add-staff-password"
                placeholder="At least 6 characters" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" />
              <label className="flex items-center gap-2 mt-2 text-xs text-[#8A8F98]">
                <input type="checkbox" className="w-4 h-4 accent-[#39FF14]" checked={form.must_reset_password}
                  onChange={(e) => setForm({ ...form, must_reset_password: e.target.checked })} data-testid="ta-add-staff-force-reset" />
                Also require the employee to change this password on first sign-in (optional)
              </label>
              <p className="text-[11px] text-[#8A8F98] mt-1">Passwords are hashed with bcrypt — the stored value is never displayed again. Use <b>Reset Password</b> later if forgotten.</p>
            </div>
          ) : (
            <div className="text-xs bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded p-3">
              Technicians <b>do not sign in</b>. They receive job assignments and updates via SMS/WhatsApp on the phone number above.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-[#3A4640] text-[#F8F9FA] rounded-[10px]">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="ta-add-staff-submit"
              className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]">Create staff</Button>
          </DialogFooter>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline gap-3 text-sm">
      <span className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading w-24 shrink-0">{label}</span>
      <span className="text-[#F8F9FA]" data-testid={testid}>{value}</span>
    </div>
  );
}

function StaffActions({ user, teams, roles, reload }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user.name || "");
  const [teamId, setTeamId] = useState(user.team_id || "");
  const [roleId, setRoleId] = useState(user.role_id || "");
  const [systemRole, setSystemRole] = useState(user.role || "sales_rep");
  const [newPass, setNewPass] = useState("");

  useEffect(() => {
    if (open) {
      setName(user.name || "");
      setTeamId(user.team_id || "");
      setRoleId(user.role_id || "");
      setSystemRole(user.role || "sales_rep");
    }
  }, [open, user.name, user.team_id, user.role_id, user.role]);

  const save = async () => {
    try {
      const patch = {};
      if ((name || "").trim() !== (user.name || "")) patch.name = name.trim();
      if (systemRole && systemRole !== user.role) patch.role = systemRole;
      if (Object.keys(patch).length) {
        await api.patch(`/users/${user.id}`, patch);
      }
      await api.patch(`/users/${user.id}/assign`, { team_id: teamId || null, role_id: roleId || null });
      toast.success("Staff updated");
      setOpen(false); reload();
    } catch (e) {
      toast.error(parseApiError(e, "Update failed"));
    }
  };
  const reset = async () => {
    if (newPass.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/users/${user.id}/reset-password`, { password: newPass });
      toast.success("Password reset");
      setNewPass("");
    } catch (e) { toast.error(parseApiError(e, "Failed")); }
  };
  const force = async () => {
    try {
      await api.post(`/users/${user.id}/force-password-reset`);
      toast.success("User will be asked to reset on next login"); reload();
    } catch (e) { toast.error(parseApiError(e, "Failed")); }
  };
  const togglePause = async () => {
    const path = user.is_paused ? "unpause" : "pause";
    try {
      await api.post(`/users/${user.id}/${path}`);
      toast.success(user.is_paused ? "Account re-enabled" : "Account disabled");
      setOpen(false); reload();
    } catch (e) {
      toast.error(parseApiError(e, "Failed"));
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`ta-manage-${user.id}`}
        className="bg-transparent border-[#3A4640] text-[#F8F9FA] hover:border-[#39FF14]/40 rounded-[10px]">Manage</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`ta-staff-dialog-${user.id}`}
          className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
          <DialogHeader><DialogTitle>{user.name || user.email}</DialogTitle>
            <DialogDescription className="text-[#8A8F98]">
              Manage name, team, role, password and account status. Last login: {formatWhen(user.last_login_at)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`ta-name-input-${user.id}`}
                className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">System role</Label>
              <Select value={systemRole} onValueChange={setSystemRole}>
                <SelectTrigger data-testid={`ta-sysrole-select-${user.id}`} className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
                  {SYSTEM_ROLES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#8A8F98] mt-1">Changes the account's base permission level.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Team</Label>
                <Select value={teamId || "__none__"} onValueChange={(v) => setTeamId(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid={`ta-team-select-${user.id}`} className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]"><SelectValue placeholder="No team" /></SelectTrigger>
                  <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
                    <SelectItem value="__none__">— No team —</SelectItem>
                    {teams.filter((t) => t.active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Custom role</Label>
                <Select value={roleId || "__none__"} onValueChange={(v) => setRoleId(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid={`ta-role-select-${user.id}`} className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]"><SelectValue placeholder="No custom role" /></SelectTrigger>
                  <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
                    <SelectItem value="__none__">— No custom role —</SelectItem>
                    {roles.filter((r) => r.active).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="pt-3 border-t border-[#3A4640]">
              <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Reset password</Label>
              <div className="flex gap-2 mt-1">
                <Input type="password" placeholder="New password (min 6)" value={newPass} onChange={(e) => setNewPass(e.target.value)}
                  className="bg-[#0B0F0C] border-[#3A4640] text-[#F8F9FA] rounded-[10px]" data-testid={`ta-new-password-${user.id}`} />
                <Button onClick={reset} data-testid={`ta-reset-btn-${user.id}`} className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]"><KeyRound size={14} className="mr-2"/>Reset</Button>
              </div>
              <Button size="sm" variant="ghost" className="mt-2 text-[#8A8F98] hover:text-[#F8F9FA]" onClick={force} data-testid={`ta-force-reset-${user.id}`}>
                <RefreshCw size={14} className="mr-2"/>Force reset on next login
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={togglePause} data-testid={`ta-pause-btn-${user.id}`}
              className={`bg-transparent border-[#3A4640] rounded-[10px] ${user.is_paused ? "text-[#39FF14] hover:border-[#39FF14]/40" : "text-yellow-300 hover:border-yellow-500/40"}`}>
              {user.is_paused ? <><UserCheck size={14} className="mr-2"/>Re-enable</> : <><UserX size={14} className="mr-2"/>Disable</>}
            </Button>
            <Button onClick={save} data-testid={`ta-save-${user.id}`} className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------ Teams ------------------ */
function TeamsTab({ teams, reload }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState(""); const [desc, setDesc] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null); // team obj
  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/teams", { name, description: desc }); toast.success("Team created"); setName(""); setDesc(""); setCreating(false); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (t) => { await api.patch(`/teams/${t.id}`, { active: !t.active }); reload(); toast.success(!t.active ? "Team activated" : "Team deactivated"); };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setCreating(true)} data-testid="ta-new-team-btn" className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]"><Plus size={14} className="mr-2"/>New Team</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {teams.length === 0 && <div className="col-span-full"><EmptyState icon={Users} title="No teams yet" description="Create your first team (e.g. Engagement, Sales, Service) to start assigning staff." testid="ta-teams-empty" /></div>}
        {teams.map((t) => (
          <div key={t.id} className="tzat-card p-5" data-testid={`ta-team-${t.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-heading font-semibold text-[#F8F9FA]">{t.name}</div>
              {t.active ? <Chip tone="green">Active</Chip> : <Chip tone="yellow">Inactive</Chip>}
            </div>
            <p className="text-xs text-[#8A8F98] mt-1 min-h-[16px]">{t.description}</p>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#3A4640]">
              <span className="text-xs text-[#8A8F98]">{t.member_count} members</span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => toggle(t)} data-testid={`ta-toggle-team-${t.id}`}
                  className="text-[#8A8F98] hover:text-[#F8F9FA]">{t.active ? "Deactivate" : "Activate"}</Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(t)} data-testid={`ta-delete-team-btn-${t.id}`}
                  className="text-red-300 hover:text-red-200 hover:bg-red-500/10"><Trash2 size={14}/></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]" data-testid="ta-new-team-dialog">
          <DialogHeader><DialogTitle>New team</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ta-new-team-name" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" /></div>
            <div><Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Description</Label>
              <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} data-testid="ta-new-team-desc" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} className="bg-transparent border-[#3A4640] text-[#F8F9FA] rounded-[10px]">Cancel</Button>
            <Button onClick={create} data-testid="ta-new-team-submit" className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeleteEntityDialog kind="team" target={deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)} reload={reload} />
    </div>
  );
}

function DeleteEntityDialog({ kind, target, onOpenChange, reload }) {
  const [members, setMembers] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!target) { setMembers(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/${kind === "team" ? "teams" : "roles"}/${target.id}/members`);
        setMembers(data);
      } catch { setMembers([]); }
    })();
  }, [target, kind]);
  if (!target) return null;
  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/${kind === "team" ? "teams" : "roles"}/${target.id}`);
      toast.success(`${kind === "team" ? "Team" : "Role"} deleted`);
      onOpenChange(false); reload();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed");
    } finally { setBusy(false); }
  };
  const doDeactivate = async () => {
    setBusy(true);
    try {
      await api.patch(`/${kind === "team" ? "teams" : "roles"}/${target.id}`, { active: false });
      toast.success(`${kind === "team" ? "Team" : "Role"} deactivated`);
      onOpenChange(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const memberCount = members?.length ?? (target.member_count || 0);
  const isSystem = kind === "role" && target.is_system;
  const canDelete = !isSystem && memberCount === 0;
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA] max-w-lg" data-testid={`ta-delete-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-200">
            <AlertTriangle size={18} className="text-red-400"/> Delete {kind} "{target.name}"?
          </DialogTitle>
          <DialogDescription className="text-[#8A8F98]">
            {isSystem ? "This is a system role and cannot be deleted." : canDelete
              ? `This ${kind} has no assignments. Deletion is permanent.`
              : `This ${kind} is currently assigned to ${memberCount} staff member${memberCount === 1 ? "" : "s"} and cannot be deleted until those assignments are changed.`}
          </DialogDescription>
        </DialogHeader>
        {members && members.length > 0 && (
          <div className="max-h-[220px] overflow-auto tzat-card p-2 space-y-1" data-testid={`ta-delete-${kind}-members`}>
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-2 py-1 text-sm">
                <div>
                  <div className="text-[#F8F9FA]">{m.name || "(no name)"}</div>
                  <div className="text-[11px] font-mono text-[#8A8F98]">{m.email}</div>
                </div>
                <Chip tone={m.is_paused ? "yellow" : "muted"}>{m.is_paused ? "Disabled" : (ROLE_LABEL[m.role] || m.role)}</Chip>
              </div>
            ))}
            <p className="text-[11px] text-[#8A8F98] pt-2 px-2">Reassign each staff member from Team & Access → Staff → Manage before deleting this {kind}.</p>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-[#3A4640] text-[#F8F9FA] rounded-[10px]">Cancel</Button>
          {!isSystem && target.active !== false && (
            <Button variant="outline" onClick={doDeactivate} disabled={busy} data-testid={`ta-${kind}-deactivate-instead`}
              className="bg-transparent border-[#3A4640] text-yellow-300 rounded-[10px]">Deactivate instead</Button>
          )}
          <Button onClick={doDelete} disabled={!canDelete || busy} data-testid={`ta-${kind}-confirm-delete`}
            className="bg-red-500 hover:bg-red-600 text-white font-semibold rounded-[10px] disabled:opacity-40">
            <Trash2 size={14} className="mr-2"/>Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------ Roles ------------------ */
function RolesTab({ roles, catalog, reload }) {
  const [editing, setEditing] = useState(null);   // role obj or {name:"", description:"", permissions:[]}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const openNew = () => setEditing({ name: "", description: "", permissions: [] });
  const save = async () => {
    if (!editing?.name?.trim()) return;
    try {
      if (editing.id) await api.patch(`/roles/${editing.id}`, editing);
      else            await api.post("/roles", editing);
      toast.success("Role saved"); setEditing(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (r) => { await api.patch(`/roles/${r.id}`, { active: !r.active }); reload(); };
  const togglePerm = (key) => {
    const has = editing.permissions.includes(key);
    setEditing({ ...editing, permissions: has ? editing.permissions.filter((k) => k !== key) : [...editing.permissions, key] });
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button onClick={openNew} data-testid="ta-new-role-btn" className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]"><Plus size={14} className="mr-2"/>New Role</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {roles.length === 0 && <div className="col-span-full"><EmptyState icon={ShieldCheck} title="No custom roles yet" description="Create a role (e.g. Engagement Executive) and choose granular permissions." testid="ta-roles-empty" /></div>}
        {roles.map((r) => (
          <div key={r.id} className="tzat-card p-5" data-testid={`ta-role-${r.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-heading font-semibold text-[#F8F9FA]">{r.name}</div>
              <div className="flex gap-1">
                {r.is_system && <Chip tone="muted">System</Chip>}
                {r.active ? <Chip tone="green">Active</Chip> : <Chip tone="yellow">Inactive</Chip>}
              </div>
            </div>
            <p className="text-xs text-[#8A8F98] mt-1 min-h-[16px]">{r.description}</p>
            <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading mt-3 mb-2">Permissions</div>
            <div className="flex flex-wrap gap-1">
              {(r.permissions || []).slice(0, 8).map((p) => <Chip key={p} tone="green">{p}</Chip>)}
              {(r.permissions || []).length > 8 && <Chip>+{r.permissions.length - 8}</Chip>}
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#3A4640]">
              <span className="text-xs text-[#8A8F98]">{r.member_count} members</span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => toggle(r)} className="text-[#8A8F98] hover:text-[#F8F9FA]" data-testid={`ta-toggle-role-${r.id}`}>{r.active ? "Deactivate" : "Activate"}</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`ta-edit-role-${r.id}`}
                  className="bg-transparent border-[#3A4640] text-[#F8F9FA] hover:border-[#39FF14]/40 rounded-[10px]">Edit</Button>
                {!r.is_system && (
                  <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(r)} data-testid={`ta-delete-role-btn-${r.id}`}
                    className="text-red-300 hover:text-red-200 hover:bg-red-500/10"><Trash2 size={14}/></Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA] max-w-3xl" data-testid="ta-role-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit role" : "New role"}</DialogTitle>
            <DialogDescription className="text-[#8A8F98]">Grant granular permissions module by module.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Name</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="ta-role-name" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" /></div>
                <div><Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Description</Label>
                  <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="ta-role-desc" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" /></div>
              </div>
              <div className="max-h-[420px] overflow-auto pr-1">
                {Object.entries(catalog).map(([mod, perms]) => (
                  <div key={mod} className="mb-4">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#39FF14] font-heading font-semibold mb-2">{mod}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                      {perms.map((p) => {
                        const checked = editing.permissions.includes(p.key);
                        return (
                          <label key={p.key} className={`flex items-center gap-2 px-3 py-2 rounded-[10px] border cursor-pointer transition-colors text-sm ${
                            checked ? "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#F8F9FA]" : "border-[#3A4640] bg-[#0B0F0C] text-[#8A8F98] hover:text-[#F8F9FA]"
                          }`} data-testid={`ta-perm-${p.key}`}>
                            <input type="checkbox" checked={checked} onChange={() => togglePerm(p.key)} className="w-4 h-4 accent-[#39FF14]" />
                            <span className="flex-1">{p.label}</span>
                            <span className="text-[10px] font-mono text-[#8A8F98]">{p.key}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="bg-transparent border-[#3A4640] text-[#F8F9FA] rounded-[10px]">Cancel</Button>
            <Button onClick={save} data-testid="ta-save-role" className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]">Save role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DeleteEntityDialog kind="role" target={deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)} reload={reload} />
    </div>
  );
}

/* ------------------ Send notification ------------------ */
function NotifyTab({ teams, roles, users }) {
  const [form, setForm] = useState({ recipient_kind: "everyone", recipient_id: "", title: "", message: "", priority: "normal", action_url: "" });
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!form.title && !form.message) return;
      try {
        const { data } = await api.post("/notifications/preview", form);
        if (alive) setPreview(data);
      } catch { if (alive) setPreview(null); }
    })();
    return () => { alive = false; };
  }, [form.recipient_kind, form.recipient_id]); // eslint-disable-line

  const send = async () => {
    if (!form.title.trim() || !form.message.trim()) { toast.error("Title and message are required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/notifications/send", form);
      toast.success(`Notification sent to ${data.count} recipient${data.count === 1 ? "" : "s"}`);
      setForm({ ...form, title: "", message: "", action_url: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      <div className="tzat-card p-6 space-y-4">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Recipient</Label>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {["everyone", "team", "role", "user"].map((k) => (
              <button key={k} type="button" onClick={() => setForm({ ...form, recipient_kind: k, recipient_id: "" })}
                data-testid={`ta-notify-recipient-${k}`}
                className={`text-xs font-heading uppercase tracking-wider px-3 py-2 rounded-[10px] border transition-colors ${
                  form.recipient_kind === k ? "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]" : "bg-[#0B0F0C] text-[#8A8F98] border-[#3A4640] hover:text-[#F8F9FA]"
                }`}>{k}</button>
            ))}
          </div>
          {form.recipient_kind !== "everyone" && (
            <Select value={form.recipient_id} onValueChange={(v) => setForm({ ...form, recipient_id: v })}>
              <SelectTrigger className="mt-2 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" data-testid="ta-notify-recipient-id">
                <SelectValue placeholder={`Choose ${form.recipient_kind}`} />
              </SelectTrigger>
              <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
                {form.recipient_kind === "team" && teams.filter((t) => t.active).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                {form.recipient_kind === "role" && roles.filter((r) => r.active).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                {form.recipient_kind === "user" && users.filter((u) => !u.is_paused).map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Title</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ta-notify-title"
            className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" placeholder="Priority: Power Tiller campaign" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Message</Label>
          <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ta-notify-message" rows={4}
            className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" placeholder="Please prioritize tomorrow's power tiller follow-ups." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" data-testid="ta-notify-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="important">Important</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Action URL (optional)</Label>
            <Input value={form.action_url} onChange={(e) => setForm({ ...form, action_url: e.target.value })} data-testid="ta-notify-action-url" placeholder="/engagement" className="mt-1 bg-[#0B0F0C] border-[#3A4640] rounded-[10px]" />
          </div>
        </div>
        <div className="pt-3 flex justify-end">
          <Button onClick={send} disabled={sending} data-testid="ta-notify-send" className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]"><Send size={14} className="mr-2"/>Send Notification</Button>
        </div>
      </div>

      <div className="tzat-card p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#39FF14] font-heading font-semibold">Preview</div>
        <div className="mt-2 text-sm text-[#F8F9FA]">
          Sending to <span className="font-mono text-[#39FF14]">{preview?.count ?? "…"}</span> recipient(s)
        </div>
        {preview?.sample?.length ? (
          <ul className="mt-3 text-xs text-[#8A8F98] space-y-1">
            {preview.sample.map((s, i) => <li key={i}>· {s}</li>)}
            {preview.count > preview.sample.length && <li>+ {preview.count - preview.sample.length} more</li>}
          </ul>
        ) : (
          <p className="mt-3 text-xs text-[#8A8F98]">Pick a recipient above to preview the audience.</p>
        )}
        <div className="mt-5 pt-3 border-t border-[#3A4640]">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading mb-1">Priority</div>
          <Chip tone={form.priority === "urgent" ? "red" : form.priority === "important" ? "yellow" : "green"}>{form.priority}</Chip>
        </div>
      </div>
    </div>
  );
}
